import Box from "@mui/material/Box";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import { Icon } from "./Icon";
import type { StepperProps, StepperStatus } from "../types";

const c = tokens.colour;
const MARK_SIZE = 32;

/** H2's PortalStepper marker: amber for the current step, navy with a tick when done, outlined
 * otherwise. */
function markStyle(status: StepperStatus) {
  const base = {
    flex: "0 0 auto",
    width: MARK_SIZE,
    height: MARK_SIZE,
    borderRadius: `${tokens.interactionState.radius.pill}px`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: tokens.typography.weights.bold,
    fontSize: 17,
    boxSizing: "border-box",
  } as const;
  if (status === "current") {
    return { ...base, backgroundColor: c.accent[500], color: c.brand[700] };
  }
  if (status === "done") {
    return { ...base, backgroundColor: c.brand[700], color: c.neutral.white };
  }
  return {
    ...base,
    backgroundColor: c.neutral.white,
    color: c.brand[500],
    border: `${tokens.interactionState.border.emphasisWidth}px solid ${c.neutral.borderControl}`,
  };
}

function statusStyle(status: StepperStatus) {
  return {
    fontSize: 14,
    color: status === "todo" ? c.neutral.inkMuted : c.neutral.ink,
    fontWeight: status === "current" ? tokens.typography.weights.bold : tokens.typography.weights.regular,
    whiteSpace: "nowrap",
  } as const;
}

/** Below 600px the labels leave the screen but stay in the accessibility tree. */
const textStyle = {
  xs: {
    position: "absolute",
    width: "1px",
    height: "1px",
    margin: "-1px",
    padding: 0,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
} as const;

/**
 * ADR-20261102-h2fd (AC-15): H2's PortalStepper. Every step is a button; a completed step with a
 * handler goes back to it, every other step is disabled. Connectors fill the space between steps.
 */
export function Stepper({ items, testId, "aria-label": ariaLabel }: Readonly<StepperProps>) {
  return (
    <Box component="nav" sx={{ padding: { xs: 0, sm: "0 4px" } }} {...cleanProps({ "aria-label": ariaLabel, "data-testid": testId })}>
      <Box
        component="ol"
        sx={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexWrap: { xs: "nowrap", sm: "wrap" },
          alignItems: "center",
          columnGap: { xs: `${tokens.spacing.xs}px`, sm: "10px" },
          rowGap: `${tokens.spacing.xs}px`,
        }}
      >
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <Box
              component="li"
              key={item.id}
              sx={{ display: "flex", alignItems: "center", listStyle: "none", flex: last ? "0 0 auto" : "1 1 auto", minWidth: 0 }}
            >
              <Box
                component="button"
                type="button"
                disabled={item.onSelect === undefined}
                onClick={item.onSelect}
                aria-current={item.status === "current" ? "step" : undefined}
                sx={{
                  appearance: "none",
                  background: "none",
                  border: "none",
                  padding: { xs: "6px", sm: "6px 4px" },
                  minHeight: tokens.interactionState.targetSize.standard,
                  minWidth: tokens.interactionState.targetSize.standard,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  flex: "0 0 auto",
                  textAlign: "left",
                  fontFamily: "inherit",
                  // H2 leaves the button's colour to the browser: black, or its faded grey when the
                  // step is disabled.
                  color: tokens.colour.h2.buttonText,
                  "&:disabled": { color: tokens.colour.h2.buttonTextDisabled },
                  cursor: item.onSelect === undefined ? "default" : "pointer",
                }}
              >
                <Box component="span" sx={markStyle(item.status)}>
                  {item.status === "done" ? <Icon name="check" size={22} /> : index + 1}
                </Box>
                <Box
                  component="span"
                  sx={{
                    ...textStyle.xs,
                    "@media (min-width:600px)": {
                      position: "static",
                      width: "auto",
                      height: "auto",
                      margin: 0,
                      overflow: "visible",
                      clip: "auto",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      lineHeight: 1.2,
                    },
                  }}
                >
                  <Box component="span" sx={{ fontSize: tokens.typography.variants.body.size, lineHeight: tokens.typography.variants.body.lineHeight, color: c.neutral.ink }}>
                    {item.label}
                  </Box>{" "}
                  <Box component="span" sx={statusStyle(item.status)}>
                    {item.statusText}
                  </Box>
                </Box>
              </Box>
              {last ? null : (
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{ display: "block", flex: { xs: "1 1 12px", sm: "1 1 24px" }, height: "2px", backgroundColor: c.brand[100] }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
